import SB from '../SchemaBuilder';
import type { Fetcher } from '../api/impl/utils/Fetcher';
import { retry } from '../utils/AsyncUtils';
import { ResponseError } from '../utils/ResponseError';

export type AirtableClient = {
    /**
     * @returns Table items or `null` if table is not found.
     * @throws On network error or error response.
     */
    getTable: (tableName: string) => Promise<{ [key: string]: unknown }[] | null>;
};

const getTableResponseSchema = SB.object({
    records: SB.array(SB.object({ fields: SB.map(SB.unknown()) }).additionalProperties()),
    offset: SB.string().optional(),
}).additionalProperties();

export function createAirtableClient(opts: {
    appId: string;
    apiKey: string;
    fetcher: Fetcher;
    retryOpts?: { exponentialBackOff: number };
}): AirtableClient {
    const headers = { Authorization: 'Bearer ' + opts.apiKey };
    const endpoint = `https://api.airtable.com/v0/${opts.appId}`;

    return {
        getTable: async (tableName) => {
            const items: { [key: string]: unknown }[] = [];
            let offset: string | undefined;

            do {
                const offsetParam = offset ? `&offset=${offset}` : '';

                // Retry on request errors, timeouts and 5xx responses:
                const response = await retry(
                    () =>
                        opts.fetcher
                            .fetch({
                                url: `${endpoint}/${tableName}?pageSize=100&view=Grid view${offsetParam}`,
                                method: 'GET',
                                headers,
                                timeout: 5_000,
                            })
                            .then(async (response) => {
                                if (response.statusCode >= 500) {
                                    throw await ResponseError.fromResponse('Airtable table fetch failed', response);
                                }

                                return response;
                            }),
                    {
                        retries: 2,
                        exponentialBackoff: opts.retryOpts ? opts.retryOpts.exponentialBackOff : 500,
                    },
                );

                if (response.statusCode < 200 || response.statusCode >= 300) {
                    throw await ResponseError.fromResponse('Airtable table fetch failed', response);
                }

                const responseBody: SB.ExtractType<typeof getTableResponseSchema> = JSON.parse(response.body);
                getTableResponseSchema.tryValidate(responseBody);

                offset = responseBody.offset;
                for (const record of responseBody.records) {
                    // Skip empty rows:
                    if (Object.keys(record.fields).length === 0) {
                        continue;
                    }

                    items.push(record.fields);
                }
            } while (offset);

            return items;
        },
    };
}

export function createStubAirtableClient(errorMessage: string): AirtableClient {
    return { getTable: () => Promise.reject(errorMessage) };
}
